import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid
} from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import InfiniteScroll from '../../components/InfiniteScroll';
import Page from '../../components/Page';
import ToggleFollow from '../../components/api/mutations/ToggleFollow';
import addDMChatRoom from '../../components/api/mutations/addDMChatRoom';
import updateUserPreferences from '../../components/api/mutations/updateUserPreferences';
import useCommentsQuery from '../../components/api/queries/useCommentsQuery';
import usePostsQuery from '../../components/api/queries/usePostsQuery';
import useUserProfileQuery from '../../components/api/queries/useUserProfileQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import useBuddyPreferenceQuery from '../../components/api/queries/useBuddyPreferenceQuery';
import AddUserProfileV1BottomSheet from '../../components/bottomSheet/AddUserProfileV1BottomSheet';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import SecondaryButton from '../../components/buttons/SecondaryButton';
import PostPreview from '../../components/community/PostPreview';
import PostsList from '../../components/community/PostsList';
import TabsListSelector from '../../components/community/TabsListSelector';
import MutualMembersBottomDrawer from '../../components/drawer/MutualMembersBottomDrawer';
import { FanBuddyCardDialog } from '../../components/fan-buddy/FanBuddyCardDialog';
import ProfileMBTIBox from '../../components/fan-buddy/ProfileMBTIBox';
import GridSkeletons from '../../components/skeletons/GridSkeletons';
import ProfileAvatar from '../../components/thumbnails/ProfileAvatar';
import { ReactComponent as CartIcon } from '../../icons/cart.fill.svg';
import { ReactComponent as CheckIcon } from '../../icons/check.svg';
import { ReactComponent as SettingIcon } from '../../icons/setting.svg';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';

const Title = styled.div`
  font-size: 26px;
  font-weight: 700;
  line-height: 32px;
  text-align: left;
  padding-right: 12px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
`;

export default function UserFeedProfilePage() {
  const queryClient = useQueryClient();
  const params = useParams();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState();
  const [mutualMembers, setMutualMembers] = useState([]);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isShowBaseProfileBottomSheet, setIsShowBaseProfileBottomSheet] = useState(false);
  const [isShowMutualMemberBottomSheet, setIsShowMutualMemberBottomSheet] = useState(false);

  const user = useUserQuery();
  const userProfile = useUserProfileQuery(params.user_profile_id);
  const buddyPreference = useBuddyPreferenceQuery();

  const handleHistoryBack = useHandleHistoryBack();

  const onSelectedTab = (tab) => {
    setSelectedTab(tab);
  };

  const invalidateUserProfile = () => {
    queryClient.invalidateQueries(['userProfile']);
    queryClient.invalidateQueries(['user']);
    queryClient.invalidateQueries(['Posts']);

    window.opener?.postMessage(
      {
        type: 'invalidated-user-profile',
        params: {
          userProfileId: params.user_profile_id
        }
      },
      window.location.origin
    );
  };

  const toggleFollow = async (isFollowed) => {
    const response = await ToggleFollow({
      isFollowed: isFollowed,
      currentUserProfileId: user?.baseUserProfile?.userProfileId,
      followUserProfileId: userProfile?.userProfileId
    });
    if (response.ok) {
      invalidateUserProfile();
    }
  };

  const startDMChat = async () => {
    try {
      const response = await addDMChatRoom({
        sourceUserProfileId: user?.baseUserProfile?.userProfileId,
        targetUserProfileId: userProfile?.userProfileId
      });

      if (response.ok) {
        const data = await response.json();
        const chatRoomId = data.chatRoom?.chatRoomId;
        if (chatRoomId) {
          navigate(`/chat_rooms/${chatRoomId}/v2`);
        }
      } else {
        console.error('Failed to create DM chat room');
      }
    } catch (error) {
      console.error('Error creating DM chat room:', error);
    }
  };

  const renderSelectedTab = () => {
    if (selectedTab?.id === 'posts') {
      return <RenderPosts user={user} userProfile={userProfile} selectedTab={selectedTab} />;
    }
    if (selectedTab?.id === 'anonymous') {
      return (
        <>
          <Box
            className="bg-surface-light dark:bg-surface-dark text-onDisabled-light dark:text-onDisabled-dark"
            textAlign={'center'}
            paddingY={'16px'}
            lineHeight={'16px'}
            fontSize={'13px'}
          >
            {t('userFeedProfilePage.anonymousOnlyToYou')}
          </Box>
          <RenderPosts user={user} userProfile={userProfile} selectedTab={selectedTab} />
        </>
      );
    }
    if (selectedTab?.id === 'comments') {
      return <RenderComments user={user} userProfile={userProfile} selectedTab={selectedTab} />;
    }
    return <></>;
  };

  const showMutualMemberBottomSheet = (members) => {
    setMutualMembers(members);
    setIsShowMutualMemberBottomSheet(true);
  };

  if (isEmpty(user) || isEmpty(userProfile)) {
    return (
      <div className="flex flex-col pt-[56px] gap-[16px] safeAreaMarginTop">
        <GridSkeletons
          gridSize={1}
          skeletonSize={1}
          skeletonHeight={'104px'}
          style={{ padding: '0px 24px' }}
        />
        <GridSkeletons
          gridSize={1}
          skeletonSize={1}
          skeletonHeight={'76px'}
          style={{ padding: '0px 24px' }}
        />
      </div>
    );
  }

  return (
    <Page
      showBackButton={true}
      noPadding={true}
      title={t('userFeedProfilePage.pageTitle')}
      onHistoryBackClick={handleHistoryBack}
      navigationElement={
        userProfile &&
        userProfile.isMine && (
          <FeedProfileMenus
            setIsShowBaseProfileBottomSheet={setIsShowBaseProfileBottomSheet}
            showTradingProfile={userProfile?.showTradingProfile ?? false}
            invalidateUserProfile={invalidateUserProfile}
          />
        )
      }
    >
      <div className="flex flex-row items-center px-[24px] pt-[12px] pb-[16px]">
        <div className="flex-1">
          <Title className="text-main-light-1 dark:text-main-dark-1">{userProfile.nickname}</Title>
          <div className="flex flex-row gap-[11px] pt-[8px] text-[13px] text-main-light-2 dark:text-main-dark-2">
            <div
              onClick={() => navigate(`/feed/user_profile/${userProfile.userProfileId}/followings`)}
            >
              <b>{`${userProfile.followingsCount} `}</b>
              {t('following')}
            </div>
            <div
              onClick={() => navigate(`/feed/user_profile/${userProfile.userProfileId}/followers`)}
            >
              <b>{`${userProfile.followersCount} `}</b>
              {t('follower')}
            </div>
          </div>
        </div>
        <div
          className="flex-shrink-0"
          onClick={() => userProfile.isMine && setIsShowBaseProfileBottomSheet(true)}
        >
          <ProfileAvatar
            profileImageUrl={userProfile?.thumbnailUrl}
            widthAndHeight={'76px'}
            svgWidthAndHeight={'48px'}
            padding={'14px'}
          />
        </div>
      </div>

      {/* {!userProfile.isMine && !isEmpty(userProfile.mutualMembers) && (
        <Flex
          paddingX={'22px'}
          paddingBottom={'8px'}
          gap={'2px'}
          onClick={() => showMutualMemberBottomSheet(userProfile.mutualMembers)}
        >
          <img src="/assets/icons/like.shape.shine.png" alt="mutual" width={20} height={20} />
          <Text
            fontSize="12px"
            lineHeight={'20px'}
            noOfLines={1}
            className="text-main-light-4 dark:text-main-dark-4"
          >
            {userProfile.mutualMembers?.length <= 2 ? (
              <Trans
                i18nKey="UserProfileSearchPage.mutualBias"
                components={{ bold: <strong /> }}
                values={{
                  members: userProfile.mutualMembers?.map((member) => member.name)?.join(', ')
                }}
              />
            ) : (
              <Trans
                i18nKey="UserProfileSearchPage.mutualBiasMore"
                components={{ bold: <strong /> }}
                values={{
                  members: `${userProfile.mutualMembers
                    ?.map((member) => member.name)
                    ?.slice(0, 2)
                    .join(', ')}`,
                  count: userProfile.mutualMembers?.length - 2
                }}
              />
            )}
          </Text>
        </Flex>
      )} */}
      {userProfile.buddyPreference && (
        <div className="px-[24px] pt-[6px] pb-[10px]">
          <ProfileMBTIBox
            mbti={userProfile.buddyPreference?.mbti}
            mutualMembers={userProfile.mutualMembers}
            isMine={userProfile.isMine}
            onClickBox={() => setIsAlertOpen(true)}
          />
        </div>
      )}
      <div className="flex flex-row items-center justify-center gap-[5px] px-[24px]">
        <div className="flex flex-grow-1 w-full">
          {
            <SimpleGrid
              width={'100%'}
              columns={
                userProfile.isMine ? 1 : userProfile.isFollowing && userProfile.isFollowed ? 2 : 1
              }
              gap={'5px'}
            >
              {!userProfile.isMine && (
                <>
                  {userProfile.isFollowed ? (
                    <SecondaryButton
                      text={userProfile.isFollowing ? t('mutualFollowing') : t('follow')}
                      icon={
                        <div style={{ paddingLeft: '4px' }}>
                          <CheckIcon
                            className="fill-primary-light dark:fill-primary-dark"
                            width={'16px'}
                            height={'16px'}
                          />
                        </div>
                      }
                      onClick={() => toggleFollow(userProfile.isFollowed)}
                      style={{
                        borderRadius: '12px',
                        padding: '11px 0px 11px 8px',
                        lineHeight: '16px'
                      }}
                    />
                  ) : (
                    <RegularSolidButton
                      text={t('follow')}
                      onClicked={() => toggleFollow(userProfile.isFollowed)}
                      style={{
                        borderRadius: '12px',
                        padding: '11px 0px',
                        lineHeight: '16px'
                      }}
                    />
                  )}
                </>
              )}
              {userProfile.isFollowing && userProfile.isFollowed && (
                <SecondaryButton
                  text={t('notifications.messagesTab')}
                  onClick={startDMChat}
                  style={{
                    borderRadius: '12px',
                    padding: '11px 0px',
                    lineHeight: '16px'
                  }}
                />
              )}
              {/* {!userProfile?.isMine && userProfile?.showTradingProfile && userProfile?.tradingUserProfileId && (
                <Box
                  className="text-pure-black dark:text-pure-white"
                  paddingY={'11px'}
                  borderRadius={'12px'}
                  backgroundImage={'/assets/backgrounds/banner_bg_holography.png'}
                  onClick={() => navigate(`/trading/user_profile/${userProfile.tradingUserProfileId}`)}
                >
                  <HolographyText className="text-pure-black">
                    {t('userFeedProfilePage.navigateTradingProfile', {
                      nickname: userProfile?.nickname || '-'
                    })}
                  </HolographyText>
                </Box>
              )} */}
            </SimpleGrid>
          }
        </div>
        {!userProfile.isMine &&
          userProfile.showTradingProfile &&
          userProfile.tradingUserProfileId && (
            <div
              className="w-[39px] h-[39px] p-[7.5px] rounded-[12px] bg-secondary-light dark:bg-secondary-dark"
              onClick={() => navigate(`/trading/user_profile/${userProfile.tradingUserProfileId}`)}
            >
              <CartIcon width={'24px'} height={'24px'} fill="url(#customGradient)" />
            </div>
          )}
      </div>
      <div className="pt-[18px] px-[24px]">
        <TabsListSelector
          onSelectedTab={onSelectedTab}
          hiddenTabId={userProfile.isMine ? '' : 'anonymous'}
        />
      </div>
      {renderSelectedTab()}
      {user.baseUserProfile && isShowBaseProfileBottomSheet && (
        <AddUserProfileV1BottomSheet
          title={t('myUserProfileList.changeProfile')}
          userProfile={user.baseUserProfile}
          isShowProfileBottomSheet={isShowBaseProfileBottomSheet}
          setIsShowProfileBottomSheet={setIsShowBaseProfileBottomSheet}
          category={'base'}
          isMergeProfile={false}
          onCompleted={invalidateUserProfile}
        />
      )}
      {isShowMutualMemberBottomSheet && (
        <MutualMembersBottomDrawer
          isOpen={isShowMutualMemberBottomSheet}
          onClose={() => setIsShowMutualMemberBottomSheet(false)}
          members={mutualMembers}
        />
      )}
      {isAlertOpen && userProfile && (
        <FanBuddyCardDialog
          isAlertOpen={isAlertOpen}
          setIsAlertOpen={setIsAlertOpen}
          isMine={userProfile.isMine}
          currentUser={user}
          myKeywords={buddyPreference?.keywords}
          buddyPreference={{
            ...userProfile.buddyPreference,
            nickname: userProfile.nickname,
            pictureUrl: userProfile.pictureUrl,
            thumbnailUrl: userProfile.thumbnailUrl,
            userProfileId: userProfile.userProfileId,
            userId: userProfile.userId,
            mutualMembers: userProfile.mutualMembers
          }}
        />
      )}
    </Page>
  );
}

const FeedProfileMenus = ({
  setIsShowBaseProfileBottomSheet,
  showTradingProfile,
  invalidateUserProfile
}) => {
  const onToggleShowTradingProfile = async () => {
    const response = await updateUserPreferences({
      showTradingProfile: !showTradingProfile
    });
    if (response?.ok) {
      invalidateUserProfile();
    }
  };

  return (
    <Menu>
      <MenuButton
        minWidth={'0px'}
        height={'18px'}
        as={IconButton}
        variant=""
        aria-label="Options"
        paddingX={'0px!important'}
        icon={
          <SettingIcon
            className="fill-main-light-1 dark:fill-main-dark-1"
            width={'24px'}
            height={'24px'}
          />
        }
      />
      <MenuList>
        <MenuItem onClick={() => setIsShowBaseProfileBottomSheet(true)}>
          {t('userFeedProfilePage.menuChangeNickname')}
        </MenuItem>
        <MenuItem onClick={() => onToggleShowTradingProfile()}>
          {showTradingProfile
            ? t('userFeedProfilePage.menuHiddenTradingProfile')
            : t('userFeedProfilePage.menuOpenTradingProfile')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

const RenderPosts = ({ user, userProfile, selectedTab }) => {
  const [paginatedPosts, setPaginatedPosts] = useState();

  const { data, isFetching, fetchNextPage, hasNextPage } = usePostsQuery({
    userProfileId: userProfile?.userProfileId,
    anonymous: selectedTab?.id === 'anonymous'
  });

  const handlePaginatedPosts = (paginatedData) => {
    const posts = paginatedData.pages?.flatMap((page) => page.posts.map((post) => post));
    setPaginatedPosts(posts);
  };
  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedPosts(data);
    }
  }, [data]);

  return (
    userProfile &&
    paginatedPosts && (
      <PostsList user={user} posts={paginatedPosts} loadMore={loadMore} hasMore={hasNextPage} />
    )
  );
};

const RenderComments = ({ user, userProfile }) => {
  const { data, isFetching, fetchNextPage, hasNextPage } = useCommentsQuery({
    userProfileId: userProfile?.userProfileId
  });

  const [paginatedComments, setPaginatedComments] = useState();

  const handlePaginatedComments = (paginatedData) => {
    const posts = paginatedData.pages?.flatMap((page) => page.comments.map((comment) => comment));
    setPaginatedComments(posts);
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedComments(data);
    }
  }, [data]);

  return (
    userProfile &&
    paginatedComments && (
      <InfiniteScroll
        items={paginatedComments}
        loadMore={fetchNextPage}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        user={user}
        renderItem={(item) => (
          <div
            className="border-b-[1px] border-separator-light-1 dark:border-separator-dark-1"
            key={item.postId}
            style={{
              padding: '16px 24px'
            }}
          >
            <PostPreview user={user} post={item} />
          </div>
        )}
      />
    )
  );
};
